/**
 * Risk Calculator Utilities
 * Handles threshold logic, composite scoring, and risk status determination
 */

// Threshold configurations
export const THRESHOLDS = {
  utilization: {
    green: { max: 80 },
    yellow: { min: 80, max: 90 },
    red: { min: 90 }
  },
  pegDeviation: {
    green: { max: 0.5 },
    yellow: { min: 0.5, max: 1 },
    red: { min: 1 }
  },
  loopExposure: {
    green: { max: 30 },
    yellow: { min: 30, max: 40 },
    red: { min: 40 }
  },
  fundingRate: {
    green: { min: 0 },
    yellow: { min: -0.5, max: 0 },
    red: { max: -0.5 }
  },
  tvlChange: {
    green: { min: 1 },
    yellow: { min: 0, max: 1 },
    red: { max: 0 }
  },
  portfolioExposure: {
    green: { max: 10 },
    yellow: { min: 10, max: 20 },
    red: { min: 20 }
  }
};

// Composite score weights
export const WEIGHTS = {
  utilization: 0.30,
  pegDeviation: 0.20,
  loopExposure: 0.15,
  fundingRate: 0.15,
  tvlChange: 0.10,
  portfolioExposure: 0.10
};

/**
 * Determine status (green/yellow/red) based on value and thresholds
 * @param {number} value - Metric value
 * @param {Object} thresholds - Threshold configuration
 * @returns {string} Status: 'green', 'yellow', or 'red'
 */
export function getStatus(value, thresholds) {
  if (value === null || value === undefined) return 'gray';

  const { green, yellow, red } = thresholds;

  // Check red first (highest priority)
  if (red.min !== undefined && value >= red.min) return 'red';
  if (red.max !== undefined && value <= red.max) return 'red';

  // Check yellow
  if (yellow.min !== undefined && yellow.max !== undefined) {
    if (value >= yellow.min && value <= yellow.max) return 'yellow';
  } else if (yellow.min !== undefined && value >= yellow.min) {
    return 'yellow';
  } else if (yellow.max !== undefined && value <= yellow.max) {
    return 'yellow';
  }

  // Default to green
  return 'green';
}

/**
 * Convert value to risk score (0-100 scale)
 * @param {number} value - Metric value
 * @param {Object} thresholds - Threshold configuration
 * @param {boolean} inverse - If true, higher values = lower risk
 * @returns {number} Risk score (0 = lowest risk, 100 = highest risk)
 */
export function valueToRiskScore(value, thresholds, inverse = false) {
  if (value === null || value === undefined) return 50; // Neutral if unavailable

  const status = getStatus(value, thresholds);
  
  let score;
  if (status === 'green') {
    score = 20; // Low risk
  } else if (status === 'yellow') {
    score = 50; // Medium risk
  } else {
    score = 85; // High risk
  }

  return inverse ? 100 - score : score;
}

/**
 * Calculate composite risk score
 * @param {Object} metrics - Object containing all metric values
 * @param {Object} portfolioData - User portfolio configuration
 * @returns {number} Composite risk score (0-100)
 */
export function calculateCompositeScore(metrics, portfolioData = {}) {
  if (!metrics) return 0;

  const scores = {
    utilization: valueToRiskScore(metrics.utilization, THRESHOLDS.utilization),
    pegDeviation: valueToRiskScore(metrics.peg?.deviation, THRESHOLDS.pegDeviation),
    loopExposure: valueToRiskScore(metrics.loopExposure, THRESHOLDS.loopExposure),
    fundingRate: valueToRiskScore(metrics.fundingRate, THRESHOLDS.fundingRate, true),
    tvlChange: valueToRiskScore(metrics.tvl?.change24h, THRESHOLDS.tvlChange, true),
    portfolioExposure: valueToRiskScore(
      portfolioData.exposurePercent,
      THRESHOLDS.portfolioExposure
    )
  };

  // Weighted average
  const composite = Object.keys(WEIGHTS).reduce((sum, key) => {
    return sum + (scores[key] || 0) * WEIGHTS[key];
  }, 0);

  return Math.round(composite);
}

/**
 * Generate alert message based on metric breach
 * @param {string} metricName - Name of the metric
 * @param {number} value - Current value
 * @param {string} status - Current status
 * @returns {Object} Alert object with severity and message
 */
export function generateAlert(metricName, value, status) {
  if (status === 'green') return null;

  const alerts = {
    utilization: {
      yellow: `Utilization at ${value.toFixed(1)}% (80-90% range). Monitor for potential liquidity constraints.`,
      red: `⚠️ CRITICAL: Utilization >90% (${value.toFixed(1)}%). Consider reducing position immediately.`
    },
    pegDeviation: {
      yellow: `mHYPER deviating ${value.toFixed(2)}% from $1 peg. Monitor liquidity.`,
      red: `⚠️ DEPEG RISK: mHYPER deviation >${value.toFixed(2)}%. Exit recommended.`
    },
    loopExposure: {
      yellow: `Loop exposure at ${value.toFixed(1)}% of TVL. Diversification recommended.`,
      red: `⚠️ HIGH CONCENTRATION: Loop exposure >${value.toFixed(1)}%. High dilution risk.`
    },
    fundingRate: {
      yellow: `Funding rate at ${value.toFixed(3)}% (near-zero or negative). Market conditions weakening.`,
      red: `⚠️ BEARISH SIGNAL: Funding rate <-0.5% (${value.toFixed(3)}%). Consider hedging.`
    },
    tvlChange: {
      yellow: `TVL change <1% in 24h (${value.toFixed(2)}%). Growth stagnating.`,
      red: `⚠️ TVL DECLINING: ${value.toFixed(2)}% change in 24h. Exit may be prudent.`
    },
    portfolioExposure: {
      yellow: `Portfolio exposure ${value.toFixed(1)}% (10-20% range). Consider diversification.`,
      red: `⚠️ OVEREXPOSED: Portfolio exposure >${value.toFixed(1)}%. Reduce to <10%.`
    }
  };

  const message = alerts[metricName]?.[status] || `${metricName} status: ${status}`;

  return {
    severity: status,
    message,
    timestamp: Date.now()
  };
}

/**
 * Get recommended action based on composite score
 * @param {number} compositeScore - Composite risk score
 * @param {Array} breachedMetrics - Array of breached metric names
 * @returns {string} Recommended action
 */
export function getRecommendedAction(compositeScore, breachedMetrics = [], t = null) {
  if (compositeScore < 30) {
    return t ? t('risk.normal') : 'Risk levels normal. Continue monitoring.';
  }

  if (compositeScore < 70) {
    return t ? t('alert.moderateRisk', { count: breachedMetrics.length }) : `Moderate risk detected (${breachedMetrics.length} metric(s) elevated). Review position sizing and consider partial exit if conditions worsen.`;
  }

  return t ? t('risk.critical', { count: breachedMetrics.length }) : `⚠️ HIGH RISK: ${breachedMetrics.length}+ metrics breached. IMMEDIATE ACTION: Reduce position to <5% of portfolio or exit entirely. Consider moving funds to diversified vaults (e.g., Gauntlet USDC).`;
}

/**
 * Format metric value for display
 * @param {number} value - Raw value
 * @param {string} type - Metric type
 * @returns {string} Formatted string
 */
export function formatMetricValue(value, type) {
  if (value === null || value === undefined) return 'N/A';

  switch (type) {
    case 'percentage':
      return `${value.toFixed(2)}%`;
    case 'currency':
      return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    case 'rate':
      return `${value > 0 ? '+' : ''}${value.toFixed(3)}%`;
    default:
      return value.toFixed(2);
  }
}

